import { useState, useEffect } from 'react';
import { Bell, Palette, Users, Building } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from './UserManagement';
import { doc, updateDoc, getDoc, collection, getDocs } from 'firebase/firestore'; // For Profile updates
import { db } from '../lib/firebase';
import PasswordChange from './PasswordChange';
import CapabilityUpload from './CapabilityUpload';

export default function Settings() {
    const { appUser, isSuperAdmin } = useAuth();
    const [activeTab, setActiveTab] = useState('profile');

    // Profile State (Contractor)
    const [profileData, setProfileData] = useState<any>(null);
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');

    // Super Admin: View As
    const [allContractors, setAllContractors] = useState<any[]>([]);
    const [selectedContractorId, setSelectedContractorId] = useState<string | null>(null);

    useEffect(() => {
        if (appUser?.contractorId) {
            setSelectedContractorId(appUser.contractorId);
        }

        if (isSuperAdmin) {
            loadAllContractors();
        }
    }, [appUser, isSuperAdmin]);

    // Effect to load profile when selection changes
    useEffect(() => {
        if (selectedContractorId) {
            loadContractorProfile(selectedContractorId);
        }
    }, [selectedContractorId]);

    const loadAllContractors = async () => {
        const querySnapshot = await getDocs(collection(db, 'contractors'));
        const contractors = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAllContractors(contractors);
    };

    const loadContractorProfile = async (id: string) => {
        try {
            const docRef = doc(db, 'contractors', id);
            const docSnap = await getDoc(docRef);
            if (docSnap.exists()) {
                setProfileData(docSnap.data());
            } else {
                setProfileData(null); // Clear if not found
            }
        } catch (error) {
            console.error("Error loading profile", error);
        }
    };

    const handleProfileUpdate = async () => {
        if (!selectedContractorId || !profileData) return;
        setLoading(true);
        try {
            await updateDoc(doc(db, 'contractors', selectedContractorId), profileData);
            setSuccessMsg("Profile updated successfully!");
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (error) {
            console.error("Update failed", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6 fade-in max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-primary">Settings</h1>
                    <p className="text-secondary text-sm">Manage your team, profile, and preferences.</p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-white/10 overflow-x-auto">
                <button
                    onClick={() => setActiveTab('profile')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'profile'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Building className="w-4 h-4" />
                        Details & AI Profile
                    </div>
                </button>

                {isSuperAdmin && (
                    <button
                        onClick={() => setActiveTab('team')}
                        className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'team'
                            ? 'border-blue-500 text-blue-400'
                            : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                            }`}
                    >
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Team Management
                        </div>
                    </button>
                )}

                <button
                    onClick={() => setActiveTab('preferences')}
                    className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${activeTab === 'preferences'
                        ? 'border-blue-500 text-blue-400'
                        : 'border-transparent text-gray-400 hover:text-white hover:border-white/20'
                        }`}
                >
                    <div className="flex items-center gap-2">
                        <Palette className="w-4 h-4" />
                        Preferences
                    </div>
                </button>
            </div>

            {/* Tab Content */}
            <div className="min-h-[400px]">

                {/* PROFILE TAB */}
                {activeTab === 'profile' && (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-in fade-in duration-300">
                        {/* Left: General Info */}
                        <div className="lg:col-span-2 space-y-6">
                            <div className="card p-6 border-white/5 bg-secondary/30">
                                <h3 className="text-lg font-bold text-white mb-6 flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2">
                                        <Building className="w-5 h-5 text-blue-400" />
                                        Company Information
                                    </div>

                                    {isSuperAdmin && (
                                        <select
                                            value={selectedContractorId || ''}
                                            onChange={(e) => setSelectedContractorId(e.target.value)}
                                            className="input-field text-xs py-1 px-2 w-48 font-normal"
                                        >
                                            <option value="" disabled>Select Profile...</option>
                                            {allContractors.map(c => (
                                                <option key={c.id} value={c.id}>{c.displayName || 'Unnamed Org'}</option>
                                            ))}
                                        </select>
                                    )}
                                </h3>

                                {!profileData ? (
                                    <div className="text-center py-10 text-gray-500">
                                        {!appUser?.contractorId ? "You are not linked to a contractor profile." : "Loading profile..."}
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-xs text-gray-400 uppercase font-bold block mb-1">Company Name</label>
                                            <input
                                                type="text"
                                                value={profileData.displayName || ''}
                                                onChange={(e) => setProfileData({ ...profileData, displayName: e.target.value })}
                                                className="input-field"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-xs text-gray-400 uppercase font-bold block mb-1">Description</label>
                                            <textarea
                                                value={profileData.description || ''}
                                                onChange={(e) => setProfileData({ ...profileData, description: e.target.value })}
                                                className="input-field h-24"
                                            />
                                        </div>
                                        {/* Capability Upload */}
                                        <div className="mb-6">
                                            <CapabilityUpload
                                                onAnalysisComplete={(data) => {
                                                    setProfileData({
                                                        ...profileData,
                                                        capabilityStatement: data.capability,
                                                        naicsCodes: [...new Set([...(profileData.naicsCodes || []), ...data.naics])],
                                                        pastPerformance: [...(profileData.pastPerformance || []), ...data.pastPerformance]
                                                    });
                                                    setSuccessMsg("Capabilities extracted! Please review and save.");
                                                }}
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 gap-6">
                                            <div>
                                                <label className="text-xs text-gray-400 uppercase font-bold block mb-1">
                                                    Capability Statement (Paste Text)
                                                </label>
                                                <textarea
                                                    value={profileData.capabilityStatement || ''}
                                                    onChange={(e) => setProfileData({ ...profileData, capabilityStatement: e.target.value })}
                                                    className="input-field h-32 font-mono text-xs"
                                                    placeholder="Paste your full Capabilities Statement here. The AI will use this to match opportunities."
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-400 uppercase font-bold block mb-1">
                                                    Past Performance (One per line)
                                                </label>
                                                <textarea
                                                    value={profileData.pastPerformance?.join('\n') || ''}
                                                    onChange={(e) => setProfileData({ ...profileData, pastPerformance: e.target.value.split('\n') })}
                                                    className="input-field h-32 font-mono text-xs"
                                                    placeholder="e.g. DHS - Cybersecurity Audit - $5M&#10;NASA - Cloud Migration - $2M"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mt-6">
                                            <div>
                                                <label className="text-xs text-gray-400 uppercase font-bold block mb-1">NAICS Codes (CSV)</label>
                                                <input
                                                    type="text"
                                                    value={profileData.naicsCodes?.join(', ') || ''}
                                                    onChange={(e) => setProfileData({ ...profileData, naicsCodes: e.target.value.split(',').map(s => s.trim()) })}
                                                    className="input-field"
                                                />
                                            </div>
                                            <div>
                                                <label className="text-xs text-gray-400 uppercase font-bold block mb-1">Set Asides (CSV)</label>
                                                <input
                                                    type="text"
                                                    value={profileData.setAsides?.join(', ') || ''}
                                                    onChange={(e) => setProfileData({ ...profileData, setAsides: e.target.value.split(',').map(s => s.trim()) })}
                                                    className="input-field"
                                                />
                                            </div>
                                        </div>

                                        <button
                                            onClick={handleProfileUpdate}
                                            disabled={loading}
                                            className="btn btn-primary mt-4"
                                        >
                                            {loading ? "Saving..." : "Save Changes"}
                                        </button>
                                        {successMsg && <span className="text-green-400 text-sm ml-4">{successMsg}</span>}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right: Security & Stats */}
                        <div className="space-y-6">
                            <PasswordChange />

                            <div className="card p-6 bg-gradient-to-br from-blue-500/10 to-purple-500/10 border-white/10">
                                <h3 className="text-sm font-bold text-gray-300 uppercase mb-4">Account Status</h3>
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                    <span className="text-white font-medium">Active Subscription</span>
                                </div>
                                <p className="text-xs text-gray-500 mb-6">Pro Plan • Renews Dec 31, 2025</p>
                                <button className="btn btn-secondary w-full text-xs">Manage Billing</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* TEAM TAB */}
                {activeTab === 'team' && isSuperAdmin && (
                    <div className="animate-in fade-in duration-300">
                        <UserManagement />
                    </div>
                )}

                {/* PREFERENCES TAB */}
                {activeTab === 'preferences' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in duration-300">
                        {/* Notifications */}
                        <div
                            onClick={() => { setSuccessMsg("Notification settings coming soon!"); setTimeout(() => setSuccessMsg(''), 3000); }}
                            className="card p-6 bg-secondary/50 backdrop-blur-sm border border-white/5 hover:border-yellow-500/50 transition-all group cursor-pointer"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-yellow-500/10 rounded-lg group-hover:bg-yellow-500/20 transition-colors">
                                    <Bell className="w-5 h-5 text-yellow-400" />
                                </div>
                                <h3 className="font-semibold text-white group-hover:text-yellow-400 transition-colors">Notifications</h3>
                            </div>
                            <p className="text-sm text-tertiary">
                                Configure email and push alerts.
                            </p>
                        </div>

                        {/* Appearance */}
                        <div
                            onClick={() => { setSuccessMsg("Theme customization coming soon!"); setTimeout(() => setSuccessMsg(''), 3000); }}
                            className="card p-6 bg-secondary/50 backdrop-blur-sm border border-white/5 hover:border-purple-500/50 transition-all group cursor-pointer"
                        >
                            <div className="flex items-center gap-3 mb-4">
                                <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                                    <Palette className="w-5 h-5 text-purple-400" />
                                </div>
                                <h3 className="font-semibold text-white group-hover:text-purple-400 transition-colors">Appearance</h3>
                            </div>
                            <p className="text-sm text-tertiary">
                                Toggle Dark/Light mode and themes.
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Global Toast Overlay */}
            {successMsg && activeTab === 'preferences' && (
                <div className="fixed bottom-8 right-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-6 py-3 rounded-xl shadow-2xl animate-in fade-in slide-in-from-bottom-4">
                    {successMsg}
                </div>
            )}
        </div>
    );
}
